import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  DatabaseOutlined,
  FileSearchOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Alert, Button, Modal, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { lineageDemoApi, LineageScanRun } from './lineageDemoApi';

interface LineageScanModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => Promise<void> | void;
}

export const LineageScanModal: React.FC<LineageScanModalProps> = ({ open, onClose, onComplete }) => {
  const [run, setRun] = useState<LineageScanRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completedRun = useRef<string | null>(null);

  useEffect(() => {
    if (!open || run?.status !== 'running') return undefined;
    const timer = window.setInterval(() => {
      void lineageDemoApi.scan(run.id).then(next => {
        setRun(next);
        if (next.status === 'succeeded' && completedRun.current !== next.id) {
          completedRun.current = next.id;
          void onComplete();
        }
      }).catch(scanError => setError(scanError instanceof Error ? scanError.message : String(scanError)));
    }, 450);
    return () => window.clearInterval(timer);
  }, [onComplete, open, run?.id, run?.status]);

  const start = async () => {
    setStarting(true);
    setError(null);
    completedRun.current = null;
    try { setRun(await lineageDemoApi.startScan()); }
    catch (scanError) { setError(scanError instanceof Error ? scanError.message : String(scanError)); }
    finally { setStarting(false); }
  };

  const close = () => {
    if (run?.status === 'running') return;
    onClose();
  };

  return (
    <Modal className="lineage-scan-modal" open={open} width={760} footer={null} onCancel={close} maskClosable={run?.status !== 'running'} destroyOnClose>
      <header className="lineage-scan-header">
        <span className="lineage-scan-icon"><FileSearchOutlined /></span>
        <div><span>SITE CATALOG REFRESH</span><h2>Qlik Sense Client-Managed scan</h2><p>Scan the ExtendBI on-premises site read-only, then add only the minimum simulated upstream needed for this demo.</p></div>
      </header>

      <div className="lineage-scan-environment">
        <DatabaseOutlined />
        <span><strong>Scanning Qlik Sense Client-Managed · On-Premises</strong><small>Certificate-authenticated QRS and Engine API access</small></span>
      </div>

      <div className="lineage-scan-boundary">
        <span><DatabaseOutlined /><strong>remote.extendbicloud.com</strong></span>
        <span><SafetyCertificateOutlined /><strong>Published Apps only</strong></span>
        <span><FileSearchOutlined /><strong>Read-only</strong></span>
      </div>

      {!run ? (
        <div className="lineage-scan-intro">
          <h3>What this scan will do</h3>
          <ol>
            <li>Inventory published Apps and read their load scripts.</li>
            <li>Collect native Qlik lineage, tables, and fields through Engine APIs.</li>
            <li>Canonicalize shared QVD references into one persistent graph.</li>
            <li>Attach visibly labeled demo-only database and 00/01 QVD context.</li>
          </ol>
          <Alert type="info" showIcon message="Hybrid evidence boundary" description="Published App and referenced QVD facts are scan-backed. Database sources and missing upstream producer stages are simulated and remain labeled in every view." />
        </div>
      ) : (
        <div className="lineage-scan-steps" aria-live="polite">
          {run.steps.map((step, index) => (
            <article key={step.id} className={`is-${step.state}`}>
              <div className="lineage-scan-step-rail"><span>{step.state === 'succeeded' ? <CheckCircleFilled /> : step.state === 'running' ? <LoadingOutlined spin /> : step.state === 'failed' ? <CloseCircleFilled /> : <ClockCircleOutlined />}</span>{index < run.steps.length - 1 ? <i /> : null}</div>
              <div><div><strong>{step.label}</strong><em className={step.evidenceClass === 'observed' ? 'is-observed' : 'is-demo'}>{step.evidenceClass === 'observed' ? 'Site evidence' : 'Demo-only'}</em></div><p>{step.description}</p><small>{step.state === 'pending' ? 'Waiting' : step.result}</small></div>
            </article>
          ))}
          {run.status === 'succeeded' && run.summary ? <div className="lineage-scan-result"><CheckCircleFilled /><span><strong>Snapshot activated</strong><small>{run.summary.scannedApps} Apps · {run.summary.modelTables} tables · {run.summary.modelFields.toLocaleString()} fields · {run.summary.nativeLineageItems} native lineage records</small></span></div> : null}
        </div>
      )}

      {run?.status === 'failed' ? <Alert className="lineage-scan-error" type="error" showIcon message="Scan could not continue" description={run.error ?? 'The previous snapshot remains active.'} /> : error ? <Alert className="lineage-scan-error" type="error" showIcon message="Scan could not continue" description={error} /> : null}
      <footer className="lineage-scan-footer">
        <span>{run?.status === 'running' ? <><Spin size="small" /> Catalog refresh in progress</> : 'No Qlik App or source data is modified.'}</span>
        {!run ? <Button type="primary" icon={<FileSearchOutlined />} loading={starting} onClick={() => void start()}>Start on-premises scan</Button> : run.status === 'succeeded' ? <><Button onClick={() => setRun(null)}>Run again</Button><Button type="primary" onClick={onClose}>View refreshed lineage</Button></> : run.status === 'failed' ? <Button type="primary" onClick={() => setRun(null)}>Try again</Button> : <Button type="primary" disabled>Scanning…</Button>}
      </footer>
    </Modal>
  );
};
